import { useEffect, useRef } from "react";
import {
  reachAndWashMaterial,
  standardCleanedMaterial,
  standardFaultyMaterial,
} from "../materials";

const isTargetMesh = (mesh) => {
  if (!mesh?.isMesh || typeof mesh?.name !== "string") {
    return false;
  }
  return mesh.name.startsWith("Drop") || mesh.name.startsWith("Skylight");
};

const normalize = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const getBaseMeshName = (meshName) => {
  if (!meshName.includes("_")) {
    return meshName;
  }
  return meshName.substring(0, meshName.indexOf("_"));
};

const getDropStatus = (drop) => drop?.status || drop?.cleanStatus || "";

const isReachAndWash = (cleaningMethod) => {
  const normalizedMethod = normalize(cleaningMethod).replace(/\s+/g, "_");
  return normalizedMethod.includes("reach") && normalizedMethod.includes("wash");
};

const getMaterialForDrop = (drop) => {
  const status = normalize(getDropStatus(drop));

  if (status === "cleaned") {
    return isReachAndWash(drop?.cleaning_method)
      ? reachAndWashMaterial
      : standardCleanedMaterial;
  }

  if (status === "faulty") {
    return standardFaultyMaterial;
  }

  return null;
};

const updateMeshMaterial = (mesh, material, originalMaterial) => {
  const targetMaterial = material || originalMaterial;
  if (!targetMaterial || mesh.material === targetMaterial) {
    return;
  }
  mesh.material = targetMaterial;
};

export function useDropsMaterialUpdater({ scene, drops, enabled, invalidate }) {
  const originalMaterialsRef = useRef(new Map());

  useEffect(() => {
    originalMaterialsRef.current = new Map();

    if (!scene) {
      return;
    }

    scene.traverse((mesh) => {
      if (!isTargetMesh(mesh)) {
        return;
      }
      originalMaterialsRef.current.set(mesh.uuid, mesh.material);
    });
  }, [scene]);

  useEffect(() => {
    if (!scene) {
      return;
    }

    const dropLookup = new Map();
    (drops || []).forEach((drop) => {
      const key = normalize(drop?.name);
      if (key) {
        dropLookup.set(key, drop);
      }
    });

    scene.traverse((mesh) => {
      if (!isTargetMesh(mesh)) {
        return;
      }

      const originalMaterial = originalMaterialsRef.current.get(mesh.uuid);
      if (!enabled) {
        updateMeshMaterial(mesh, null, originalMaterial);
        return;
      }

      const fullName = normalize(mesh.name);
      const baseName = normalize(getBaseMeshName(mesh.name));
      const drop = dropLookup.get(fullName) || dropLookup.get(baseName);

      if (!drop) {
        updateMeshMaterial(mesh, null, originalMaterial);
        return;
      }

      updateMeshMaterial(mesh, getMaterialForDrop(drop), originalMaterial);
    });

    invalidate?.();
  }, [scene, drops, enabled, invalidate]);
}
